import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  RenumberInstallmentRequest,
  ChangeDueDateRequest,
  DeleteInstallmentRequest,
  ChangeStatusRequest,
  AttachReceiptRequest,
  PunishRemainingRequest,
  MoveToVersionRequest,
  MoveBatchToVersionRequest,
  ActivateInstallmentRequest,
  ActivateInstallmentsBatchRequest,
  AdminInstallmentActionResponse
} from './payment-plan-admin.service';

/**
 * 🔐 Espejo corporativo de PaymentPlanAdminService. Mismas acciones
 * (renumber, change-date, delete, change-status, attach-receipt,
 * recalculate-status, punish-remaining) pero contra cuotas
 * CorporatePaymentPlan. Protegido en backend por email
 * (solo jdanigo@cloudsoft.site).
 */
@Injectable({ providedIn: 'root' })
export class CorporatePaymentPlanAdminService {
  private readonly baseUrl = `${environment.apiUrl}/admin/corporate-payment-plans`;

  constructor(private http: HttpClient) {}

  renumber(installmentId: number, request: RenumberInstallmentRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/renumber`, request);
  }

  changeDueDate(installmentId: number, request: ChangeDueDateRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/change-due-date`, request);
  }

  delete(installmentId: number, request: DeleteInstallmentRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/delete`, request);
  }

  changeStatus(installmentId: number, request: ChangeStatusRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/change-status`, request);
  }

  attachReceipt(installmentId: number, request: AttachReceiptRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/attach-receipt`, request);
  }

  recalculateStatus(installmentId: number)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/recalculate-status`, {});
  }

  /** 🆕 Recalcula PaymentStatus de todas las cuotas activas del acuerdo en un scope. */
  recalculateAll(agreementId: number, scope: 'company' | 'participants', participantId?: number): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/agreements/${agreementId}/recalculate-all`,
      { scope, participantId: participantId ?? null });
  }

  /** 🆕 Cuotas eliminadas (papelera). Permiso: ENROLLMENTS_PAYMENT_PLAN_TRASH. */
  getTrash(agreementId: number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/agreements/${agreementId}/trash`);
  }

  /** 🆕 Restaura una cuota corporativa eliminada a la versión activa de su scope. */
  restore(installmentId: number, comment: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/restore`, { comment });
  }

  punishRemaining(installmentId: number, request: PunishRemainingRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/punish-remaining`, request);
  }

  moveToVersion(installmentId: number, request: MoveToVersionRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/move-to-version`, request);
  }

  /** 🆕 Batch move corporativo (todas las cuotas deben compartir scope). */
  moveBatchToVersion(request: MoveBatchToVersionRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/move-batch-to-version`, request);
  }

  /**
   * 🆕 Reordena las cuotas activas del acuerdo corporativo. El scope (participantId
   * + payerType) se infiere del primer id de la lista en el backend.
   */
  reorder(corporateEnrollmentId: number, orderedIds: number[])
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(
      `${this.baseUrl}/corporate-enrollments/${corporateEnrollmentId}/reorder`,
      { orderedIds }
    );
  }

  /**
   * 🆕 Activa manualmente una cuota corporativa archivada dentro de su versión
   * actual. Renumera por DueDate dentro del scope.
   */
  activate(installmentId: number, request: ActivateInstallmentRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/${installmentId}/activate`, request);
  }

  /**
   * 🆕 Activa N cuotas corporativas archivadas en una sola transacción. Todas
   * deben compartir acuerdo + scope (participantId + payerType).
   */
  activateBatch(request: ActivateInstallmentsBatchRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(`${this.baseUrl}/activate-batch`, request);
  }

  /**
   * 🆕 Promueve un grupo virtual del historial corporativo (cuotas con
   * CorporatePaymentPlanVersionId=null agrupadas por (Version, ParticipantId))
   * a una CorporatePaymentPlanVersion real y activa. El Scope se infiere
   * automáticamente en el backend.
   */
  promoteGroupToVersion(corporateEnrollmentId: number, request: PromoteGroupToCorporateVersionRequest)
    : Observable<{ data: AdminInstallmentActionResponse } | AdminInstallmentActionResponse> {
    return this.http.post<any>(
      `${this.baseUrl}/enrollments/${corporateEnrollmentId}/promote-group-to-version`,
      request
    );
  }
}

/** 🆕 Request para promover grupo virtual corporativo. */
export interface PromoteGroupToCorporateVersionRequest {
  /** Valor de CorporatePaymentPlan.Version del grupo a promover. */
  legacyVersion: number;
  /** Null para grupos de empresa/all; int para grupos de un participante. */
  participantId: number | null;
  comment: string;
  /**
   * Si true (default), barre también las demás cuotas huérfanas del MISMO scope
   * inferido + mismo participantId (otros valores de Version legacy).
   */
  sweepOtherOrphans?: boolean;
}
